import { useCallback, useEffect } from "react"
import { Key, KeyControl, ModifierKey } from "./types"

export const parseKeyControl = (key: KeyControl) => {
  // we need to be able to handle combinations like "shift+enter"
  const keys = key.split("+")
  const modifiers = keys.slice(0, keys.length - 1) as ModifierKey[]
  const keyName = keys[keys.length - 1] as Key
  return { modifiers, keyName }
}

const modifierKeys: ModifierKey[] = ["Meta", "Control", "Alt", "Shift"]

const useKeyControl = (
  key: KeyControl | KeyControl[],
  handler: (e: KeyboardEvent) => void
) => {
  const keyHandler = useCallback(
    (e: KeyboardEvent) => {
      const keys = Array.isArray(key) ? key : [key]

      keys.forEach((key) => {
        // Only proceeding if the event has not been prevented already by previous iteration.
        if (!e.defaultPrevented) {
          const { modifiers, keyName } = parseKeyControl(key)

          // Ensure the event is a KeyboardEvent (and not some other event, like Event).
          // This is entirely possible and can occur, for instance, during form autocompletion.
          if (e instanceof KeyboardEvent) {
            if (keyName.toLowerCase() !== e.key?.toLowerCase()) return

            const modifiersMatch = modifierKeys.every(
              (modifier) =>
                // All included modifiers must be active.
                (modifiers.includes(modifier) &&
                  e.getModifierState(modifier)) ||
                // All excluded modifiers must be inactive.
                (!modifiers.includes(modifier) && !e.getModifierState(modifier))
            )

            if (!modifiersMatch) return

            handler(e)
            // Prevent same handler from being called multiple times (in the off-chance).
            e.preventDefault()
          }
        }
      })
    },
    [key, handler]
  )

  useEffect(() => {
    window.addEventListener("keydown", keyHandler)
    return () => window.removeEventListener("keydown", keyHandler)
  }, [key, handler])
}

export default useKeyControl
